import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material';
import { Route, Switch, useRouteMatch } from 'react-router-dom';

import PageWithDataWrapper from '../../../../components/PageWithDataWrapper';
import StyledHeader from '../StyledHeader/StyledHeader';
import useGoBack from '../../../../hooks/useGoBack';
import CalendarPage from '../CalendarPage/CalendarPage';
import RequiredInfoPage from '../RequiredInfoPage/RequiredInfoPage';
import ConfirmationPage from '../ConfirmationPage/ConfirmationPage';
import ShippingPage from '../ShippingPage/ShippingPage';
import useGiftStatuses from '../../hooks/useGiftStatuses';
import GiftStatusBar from '../../../../components/GiftStatusBar/GiftStatusBar';
import { getProduct } from '../../store/giftFlow/giftFlow.selectors';
import useResponsive from '../../../../hooks/useResponsive';
import NoMatchRoute from '../../../../components/Shared/NoMatchRoute';
import useTrackRecipient from '../../../../hooks/useTrackRecipient';
import { getIsGiftDataLoaded } from '../../store/recipient.selectors';
import { getSenderState } from '../../../../store/users/users.selectors';
import ChiliPiperCalendarPage from '../ChiliPiperCalendarPage/ChiliPiperCalendarPage';
import { getGiftCalendarProvider } from '../../store/data/data.selectors';

import FixedLayout from './FixedLayout/FixedLayout';
import AcceptLayout from './AcceptLayout/AcceptLayout';
import RecipientInfoWrapper from './RecipientInfoWrapper/RecipientInfoWrapper';
import SenderInfo from './SenderInfo/SenderInfo';

export interface IAcceptPageProps {
  parentUrl: string;
}

const AcceptPage = ({ parentUrl }: IAcceptPageProps): JSX.Element => {
  const { path } = useRouteMatch();
  const { isMobile } = useResponsive();
  const { palette } = useTheme();
  const trackEvent = useTrackRecipient();

  const isGiftDataLoaded = useSelector(getIsGiftDataLoaded);
  const sender = useSelector(getSenderState);
  const flowProduct = useSelector(getProduct);

  const calendarProvider = useSelector(getGiftCalendarProvider);
  const isChiliPiperIntegration = calendarProvider === 'chilipiper';

  const { statuses, activeStatus, previousStepUrl } = useGiftStatuses();
  const handleGoBack = useGoBack(previousStepUrl);

  const header = useMemo(
    () => (
      <StyledHeader onBackClick={handleGoBack}>
        <GiftStatusBar statuses={statuses} activeStatus={activeStatus} showStatuses={!isMobile} />
      </StyledHeader>
    ),
    [handleGoBack, statuses, activeStatus, isMobile],
  );

  const handleEmailClick = useCallback(() => {
    trackEvent('Contact - Clicked email link', { product: flowProduct });
  }, [trackEvent, flowProduct]);

  const handlePhoneClick = useCallback(() => {
    trackEvent('Contact - Clicked phone link', { product: flowProduct });
  }, [trackEvent, flowProduct]);

  const handleOpenFunFacts = useCallback(() => {
    trackEvent('Contact - Opened fun facts tab', { product: flowProduct });
  }, [trackEvent, flowProduct]);

  const userInfo = useMemo(
    () => (
      <RecipientInfoWrapper
        bgcolor={isMobile ? 'common.white' : 'customStyling.backgroundColor'}
        borderBottom={isMobile ? `2px solid ${palette.grey.dark}` : ''}
        height={isMobile ? 'auto' : '100%'}
      >
        {sender && (
          <SenderInfo
            sender={sender}
            onEmailClick={handleEmailClick}
            onPhoneClick={handlePhoneClick}
            onOpenFunFacts={handleOpenFunFacts}
          />
        )}
      </RecipientInfoWrapper>
    ),
    [sender, handleEmailClick, handlePhoneClick, handleOpenFunFacts, isMobile, palette],
  );

  return (
    <PageWithDataWrapper isLoaded={isGiftDataLoaded}>
      <Switch>
        <Route exact path={`${path}/schedule`}>
          <FixedLayout header={header} infoContent={isChiliPiperIntegration ? null : userInfo}>
            {isChiliPiperIntegration ? <ChiliPiperCalendarPage /> : <CalendarPage />}
          </FixedLayout>
        </Route>
        <Route exact path={`${path}/information`}>
          <AcceptLayout header={header} infoContent={userInfo} leftWidth={4} rightWidth={8}>
            <RequiredInfoPage />
          </AcceptLayout>
        </Route>
        <Route path={`${path}/shipping`} exact>
          <AcceptLayout header={header}>
            <ShippingPage parentUrl={parentUrl} />
          </AcceptLayout>
        </Route>
        <Route path={`${path}/confirmation`} exact render={() => <ConfirmationPage parentUrl={parentUrl} />} />
        <Route path="*" component={NoMatchRoute} />
      </Switch>
    </PageWithDataWrapper>
  );
};

export default AcceptPage;
